<script setup>
import { Icon } from '@iconify/vue2'
import { useOffsetPagination } from '@vueuse/core'
import { computed, toRefs, watch } from 'vue'

const emit = defineEmits(['paginationSettings'])

const props = defineProps({
	pageSize: Number,
	page: Number,
	total: Number,
	hideLineCount: Boolean,
})

const { pageSize, page, total } = toRefs(props)

const { currentPage, currentPageSize, pageCount, isFirstPage, isLastPage, prev, next } =
	useOffsetPagination({
		total: total.value,
		page: page.value,
		pageSize: pageSize.value,
	})

watch([currentPage, currentPageSize], () => {
	emit('paginationSettings', {
		currentPageSize: currentPageSize.value,
		currentPage: currentPage.value,
	})
})

const paginationItems = computed(() => {
	if (pageCount.value <= 3) {
		return Array(pageCount.value)
			.fill()
			.map((e, i) => i + 1)
	} else {
		if (isFirstPage.value) {
			// return [currentPage.value, currentPage.value + 1, "...", pageCount.value]
			return [1, currentPage.value + 1, '...', pageCount.value]
		} else if (currentPage.value < pageCount.value - 1) {
			// return [currentPage.value - 1, currentPage.value, "...", pageCount.value]
			return [1, currentPage.value, '...', pageCount.value]
		} else {
			return [1, '...', pageCount.value - 1, pageCount.value]
		}
	}
})

const firstIndex = computed(() => {
	return currentPageSize.value * currentPage.value - (currentPageSize.value - 1)
})

const lastIndex = computed(() => {
	return currentPageSize.value * currentPage.value > props.total
		? props.total
		: currentPageSize.value * currentPage.value
})
</script>

<template>
	<div
		style="display: flex"
		class="w-full justify-between items-center"
	>
		<p class="text-sm !m-0">
			Mostrando {{ firstIndex }} - {{ lastIndex }} de {{ total }} registros
		</p>
		<div
			style="display: flex"
			class="gap-2 items-center"
		>
			<div
				class="text-sm"
				v-if="!hideLineCount"
			>
				Exibir
				<input
					id=""
					v-model="currentPageSize"
					class="w-8 mx-2 text-center outline outline-1 outline-offset-1 rounded"
					type="number"
					:max="total"
					name=""
				/>
				linhas
			</div>
			<div
				style="display: flex"
				class="gap-2 items-center box-border"
			>
				<button
					:disabled="isFirstPage"
					class="icon_container"
					@click="prev()"
				>
					<Icon icon="ph:caret-left-bold" />
				</button>
				<span
					v-for="item in paginationItems"
					:key="item"
					:class="{ active: item === currentPage, item: item != '...' }"
					@click="currentPage = item"
				>
					{{ item }}</span
				>
				<button
					:disabled="isLastPage"
					class="icon_container"
					@click="next()"
				>
					<Icon icon="ph:caret-right-bold" />
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped>
.item {
	@apply cursor-pointer hover:outline hover:outline-1 hover:outline-offset-1 px-2.5 py-0.5 rounded text-[#34617E] box-border text-sm;
}

.active {
	@apply bg-[#34617E] text-white-neutral;
}

.icon_container {
	@apply hover:outline hover:outline-1 hover:outline-offset-1 text-[#34617E] p-1.5 flex items-center rounded-full cursor-pointer;
}

.icon_container:disabled {
	opacity: 0.4;
}

.icon_container:disabled:hover {
	outline: none;
	cursor: not-allowed;
}

input {
	-webkit-appearance: none;
	-moz-appearance: textfield;
	appearance: none;
}
</style>
